import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ChevronRight,
  BarChart3,
  History,
  LayoutDashboard,
  Settings,
  HelpCircle,
  LogOut,
  Flame,
  Check,
  Volume2,
  Vibrate,
  Bell,
  Globe,
  Shield,
  FileText,
  UserPlus,
  Eye,
  Coins,
  MessageCircle,
  Mail,
  CircleHelp,
  AlertTriangle,
  QrCode,
  Copy,
  CheckCircle,
  ArrowLeft,
  ArrowRight,
  Trophy,
  TrendingUp,
  Target,
} from 'lucide-react'
import { Sun, Moon, Monitor } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'
import { pageContainer, fadeInUp, cardReveal, listItem, hoverLift, tapScale } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useAuthStore } from '@/stores/authStore'
import { useThemeStore } from '@/stores/themeStore'
import { useCollapsibleHeader } from '@/hooks/useCollapsibleHeader'

// Mock user data
const mockUser = {
  name: '張小明',
  id: '888888',
  avatar: '🎭',
  country: '🇹🇼',
  memberSince: '2024年1月',
  isAgent: true,
  isAdmin: true,
  level: 'VIP 鑽石',
}

// Mock stats
const mockStats = {
  totalHands: 1247,
  winRate: 58,
  currentStreak: 3,
  netProfit: 125000,
}

// Languages - only Traditional Chinese and English per requirements
const languages = [
  { code: 'zh-TW', label: '繁體中文', flag: '🇹🇼' },
  { code: 'en', label: 'English', flag: '🇺🇸' },
]

// Support options
const supportOptions = [
  { icon: CircleHelp, label: '常見問題', description: '瀏覽 FAQ 解答', action: 'faq' },
  { icon: MessageCircle, label: '線上客服', description: '即時聊天支援', action: 'chat' },
  { icon: Mail, label: '電子郵件', description: 'support@pwglounge.com', action: 'email' },
  { icon: AlertTriangle, label: '提交申訴', description: '回報問題或爭議', action: 'dispute-navigate' },
  { icon: FileText, label: '我的申訴紀錄', description: '查看申訴進度', action: 'disputes-list' },
]

export function ProfileScreen() {
  const navigate = useNavigate()
  const { headerRef, isMinimized } = useCollapsibleHeader()

  // Drawer states
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [supportOpen, setSupportOpen] = useState(false)
  const [languageOpen, setLanguageOpen] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [agentActivateOpen, setAgentActivateOpen] = useState(false)
  const [twoFAOpen, setTwoFAOpen] = useState(false)
  // 2FA flow state
  const [twoFAStep, setTwoFAStep] = useState(1)
  const [twoFACode, setTwoFACode] = useState(['', '', '', '', '', ''])
  const [twoFACodeCopied, setTwoFACodeCopied] = useState(false)
  const mockBackupCodes = ['A7K2-M9X1', 'B3P5-Q8W4', 'C1R6-N2Y7', 'D8S3-T5Z9', 'E4U1-V6J8', 'F9H2-L3K5', 'G6W7-X1M4', 'H2Y8-P4N6']

  // Settings state
  const [selectedLanguage, setSelectedLanguage] = useState('zh-TW')
  const [settings, setSettings] = useState({
    twoFactorAuth: false,
    soundEffects: true,
    vibration: true,
    autoMuck: false,
    betConfirmation: 50,
    jackpotOptIn: true,
    jackpotAutoJoin: true,
    jackpotAmount: 0.5,
    notifyJackpot: true,
    notifyRoomInvites: true,
    notifyTransactions: true,
  })

  // Menu items based on PRD
  const menuItems = [
    { icon: BarChart3, label: '統計數據', description: '查看遊戲表現', action: 'navigate', path: '/stats' },
    { icon: History, label: '手牌歷史', description: '瀏覽過往手牌', action: 'navigate', path: '/history' },
    { icon: Bell, label: '通知紀錄', description: '查看所有通知歷史', action: 'navigate', path: '/notifications' },
    ...(mockUser.isAgent
      ? [{ icon: LayoutDashboard, label: '代理儀表板', description: '管理房間與收益', action: 'navigate', path: '/agent', badge: '代理' }]
      : [{ icon: UserPlus, label: '成為代理', description: '開始賺取佣金', action: 'agent-activate' }]
    ),
    ...(mockUser.isAdmin
      ? [{ icon: Shield, label: '管理後台', description: '系統管理與監控', action: 'navigate', path: '/admin', badge: '管理員' }]
      : []
    ),
    { icon: Settings, label: '設定', description: '帳戶與應用程式偏好', action: 'settings' },
    { icon: HelpCircle, label: '支援', description: 'FAQ 與客服', action: 'support' },
  ]

  const handleMenuClick = (item: typeof menuItems[0]) => {
    switch (item.action) {
      case 'navigate':
        navigate(item.path!)
        break
      case 'settings':
        setSettingsOpen(true)
        break
      case 'support':
        setSupportOpen(true)
        break
      case 'agent-activate':
        setAgentActivateOpen(true)
        break
    }
  }

  const toggleSetting = (key: keyof typeof settings) => {
    setSettings(s => ({ ...s, [key]: !s[key] }))
  }

  return (
    <motion.div
      variants={pageContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col min-h-full bg-background relative overflow-x-hidden"
    >
      {/* Ambient glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[300px] gradient-radial-gold pointer-events-none" />
      <div className="fixed inset-0 noise-overlay pointer-events-none" />

      {/* Expanded Header — normal flow, scrolls away */}
      <header ref={headerRef} className="relative z-10 px-6 pt-4">
        <motion.h1 variants={fadeInUp} className="font-display text-3xl font-semibold text-foreground mb-6">個人資料</motion.h1>

        {/* Profile Card */}
        <motion.div
          variants={cardReveal}
          className="p-6 glass-card-elevated rounded-3xl mb-6 relative overflow-hidden"
        >
          <div className="relative z-10 flex items-center gap-5">
            <div className="relative">
              <div className="absolute -inset-1 rounded-full bg-gradient-to-r from-gold via-gold-deep to-gold opacity-70" />
              <div className="relative w-20 h-20 rounded-full bg-surface-elevated border-2 border-gold/30 flex items-center justify-center text-4xl shadow-glow-gold">
                {mockUser.avatar}
              </div>
            </div>

            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="font-display text-xl font-semibold text-foreground">{mockUser.name}</h2>
                <span className="text-lg">{mockUser.country}</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-mono text-muted-foreground">ID: {mockUser.id}</span>
                {mockUser.isAgent && (
                  <span className="px-2 py-0.5 bg-gold/10 text-gold border border-gold/20 text-[10px] rounded-full font-bold uppercase tracking-wider">
                    代理
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">加入於 {mockUser.memberSince}</p>
            </div>
            <motion.button
              whileTap={tapScale}
              onClick={() => setSettingsOpen(true)}
              className="w-10 h-10 rounded-full glass-card flex items-center justify-center text-muted-foreground hover:text-gold hover:border-gold/30 transition-colors duration-200"
            >
              <Settings className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>

        {/* Quick Stats — 2x2 larger cards */}
        <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-3 mb-2">
          {[
            { value: mockStats.totalHands.toLocaleString(), label: '總手牌', color: 'text-foreground', icon: Target, iconColor: 'text-blue-400', bgGlow: 'from-blue-500/5' },
            { value: `${mockStats.winRate}%`, label: '勝率', color: 'text-emerald-400', icon: TrendingUp, iconColor: 'text-emerald-400', bgGlow: 'from-emerald-500/5' },
            { value: String(mockStats.currentStreak), label: '連勝', color: 'text-gold', icon: Flame, iconColor: 'text-gold', bgGlow: 'from-gold/5' },
            { value: `${mockStats.netProfit >= 0 ? '+' : ''}$${(Math.abs(mockStats.netProfit) / 100).toFixed(0)}`, label: '淨利潤', color: mockStats.netProfit >= 0 ? 'text-foreground' : 'text-rose-400', icon: Trophy, iconColor: 'text-purple-400', bgGlow: 'from-purple-500/5' },
          ].map((stat, i) => (
            <motion.div
              key={i}
              variants={cardReveal}
              whileHover={hoverLift}
              className={cn(
                "p-4 glass-card rounded-2xl flex flex-col gap-2 relative overflow-hidden",
                `bg-gradient-to-br ${stat.bgGlow} to-transparent`
              )}
            >
              <div className="flex items-center gap-2">
                <stat.icon className={cn('w-4 h-4', stat.iconColor)} />
                <span className="text-[11px] text-muted-foreground uppercase tracking-wider font-semibold">{stat.label}</span>
              </div>
              <p className={cn('text-2xl font-bold font-mono', stat.color)}>
                {stat.value}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </header>

      {/* Minimized sticky bar — overlays content, no layout space */}
      <div className="sticky top-0 z-40 h-0">
        <AnimatePresence>
          {isMinimized && (
            <motion.div
              initial={{ y: -48, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -48, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
              className="px-4 py-2 flex items-center justify-between bg-background/95 backdrop-blur-xl border-b border-foreground/5"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-surface-elevated border border-gold/20 flex items-center justify-center text-lg">
                  {mockUser.avatar}
                </div>
                <span className="font-semibold text-sm text-foreground">{mockUser.name}</span>
                {mockUser.isAgent && (
                  <span className="px-1.5 py-0.5 bg-gold/10 text-gold border border-gold/20 text-[9px] rounded-full font-bold uppercase">
                    代理
                  </span>
                )}
              </div>
              <motion.button
                whileTap={tapScale}
                onClick={() => setSettingsOpen(true)}
                className="w-8 h-8 rounded-full glass-card flex items-center justify-center text-muted-foreground hover:text-gold hover:border-gold/30 transition-colors duration-200"
              >
                <Settings className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Menu Items */}
      <motion.main
        variants={pageContainer}
        initial="hidden"
        animate="visible"
        className="px-6 pb-24 space-y-2 mt-4 relative z-10"
      >
        {menuItems.map((item) => (
          <motion.button
            variants={listItem}
            key={item.label}
            onClick={() => handleMenuClick(item)}
            whileHover={hoverLift}
            whileTap={tapScale}
            className="flex items-center justify-between w-full p-4 glass-card rounded-2xl hover:border-gold/20 transition-colors duration-200 group"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-surface-elevated flex items-center justify-center group-hover:bg-gold/10 transition-colors duration-200">
                <item.icon className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors duration-200" />
              </div>
              <div className="text-left">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-sm text-foreground/80 group-hover:text-foreground transition-colors duration-200">{item.label}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 bg-gold/10 text-gold border border-gold/20 text-[10px] rounded-full font-bold">
                      {item.badge}
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{item.description}</span>
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors duration-200" />
          </motion.button>
        ))}

        {/* Logout Button */}
        <motion.button
          variants={fadeInUp}
          whileTap={tapScale}
          onClick={() => setLogoutOpen(true)}
          className="flex items-center justify-center gap-2 w-full mt-6 p-4 text-rose-500/80 hover:text-rose-400 hover:bg-rose-500/10 rounded-2xl transition-colors duration-200 border border-transparent hover:border-rose-500/20 font-bold text-sm"
        >
          <LogOut className="w-4 h-4" />
          <span>登出帳號</span>
        </motion.button>
      </motion.main>

      {/* Settings Drawer */}
      <Drawer.Root open={settingsOpen} onOpenChange={setSettingsOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh] flex flex-col" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 flex flex-col flex-1 overflow-hidden">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-4" />
              <div className="px-6 pb-2">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground">設定</Drawer.Title>
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-8 safe-area-bottom">
                {/* Account Settings */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">帳戶</p>
                  <div className="space-y-2">
                    <button
                      onClick={() => {
                        setSettingsOpen(false)
                        setTimeout(() => {
                          setTwoFAStep(1)
                          setTwoFACode(['', '', '', '', '', ''])
                          setTwoFACodeCopied(false)
                          setTwoFAOpen(true)
                        }, 200)
                      }}
                      className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <Shield className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                        <div className="text-left">
                          <p className="font-semibold text-sm text-foreground">兩步驟驗證</p>
                          <p className="text-xs text-muted-foreground">使用驗證器 App 保護帳戶</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {settings.twoFactorAuth && (
                          <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-[10px] rounded-full font-bold">
                            已啟用
                          </span>
                        )}
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                      </div>
                    </button>
                  </div>
                </div>

                {/* Game Settings */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">遊戲</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Volume2 className="w-5 h-5 text-muted-foreground" />
                        <span className="font-semibold text-sm text-foreground">音效</span>
                      </div>
                      <button
                        onClick={() => toggleSetting('soundEffects')}
                        className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings.soundEffects ? "bg-gold" : "bg-foreground/10")}
                      >
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings.soundEffects ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Vibrate className="w-5 h-5 text-muted-foreground" />
                        <span className="font-semibold text-sm text-foreground">震動</span>
                      </div>
                      <button
                        onClick={() => toggleSetting('vibration')}
                        className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings.vibration ? "bg-gold" : "bg-foreground/10")}
                      >
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings.vibration ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Eye className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-semibold text-sm text-foreground">自動蓋牌</p>
                          <p className="text-xs text-muted-foreground">輸牌時自動隱藏手牌</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleSetting('autoMuck')}
                        className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings.autoMuck ? "bg-gold" : "bg-foreground/10")}
                      >
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings.autoMuck ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Jackpot Settings */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">獎池</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Coins className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-semibold text-sm text-foreground">預設參與</p>
                          <p className="text-xs text-muted-foreground">每手自動參與獎池</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleSetting('jackpotOptIn')}
                        className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings.jackpotOptIn ? "bg-gold" : "bg-foreground/10")}
                      >
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings.jackpotOptIn ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                    <div className="flex items-center justify-between p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3">
                        <Trophy className="w-5 h-5 text-muted-foreground" />
                        <div>
                          <p className="font-semibold text-sm text-foreground">累積獎池自動加入</p>
                          <p className="text-xs text-muted-foreground">加入新桌時自動參與累積獎池</p>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleSetting('jackpotAutoJoin')}
                        className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings.jackpotAutoJoin ? "bg-gold" : "bg-foreground/10")}
                      >
                        <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings.jackpotAutoJoin ? "translate-x-6" : "translate-x-1")} />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">通知</p>
                  <div className="space-y-2">
                    {[
                      { key: 'notifyJackpot' as const, label: '獎池中獎' },
                      { key: 'notifyRoomInvites' as const, label: '房間邀請' },
                      { key: 'notifyTransactions' as const, label: '交易更新' },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center justify-between p-4 glass-card rounded-2xl">
                        <div className="flex items-center gap-3">
                          <Bell className="w-5 h-5 text-muted-foreground" />
                          <span className="font-semibold text-sm text-foreground">{label}</span>
                        </div>
                        <button
                          onClick={() => toggleSetting(key)}
                          className={cn("w-12 h-7 rounded-full transition-colors duration-200 relative", settings[key] ? "bg-gold" : "bg-foreground/10")}
                        >
                          <div className={cn("absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-transform duration-200", settings[key] ? "translate-x-6" : "translate-x-1")} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Other Settings */}
                <div className="mb-6">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-3">其他</p>
                  <div className="space-y-2">
                    {/* Theme Toggle */}
                    <div className="p-4 glass-card rounded-2xl">
                      <div className="flex items-center gap-3 mb-3">
                        <Sun className="w-5 h-5 text-muted-foreground" />
                        <span className="font-semibold text-sm text-foreground">外觀</span>
                      </div>
                      <ThemeToggle />
                    </div>

                    <button
                      onClick={() => {
                        setSettingsOpen(false)
                        setTimeout(() => setLanguageOpen(true), 200)
                      }}
                      className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group"
                    >
                      <div className="flex items-center gap-3">
                        <Globe className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                        <span className="font-semibold text-sm text-foreground">語言</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{languages.find(l => l.code === selectedLanguage)?.label}</span>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                      </div>
                    </button>
                    <button className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                        <span className="font-semibold text-sm text-foreground">服務條款</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                    </button>
                    <button className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                        <span className="font-semibold text-sm text-foreground">隱私政策</span>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                    </button>
                  </div>
                </div>

                <p className="text-center text-xs text-muted-foreground">
                  版本 1.0.0 • PWG Lounge
                </p>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Language Drawer */}
      <Drawer.Root open={languageOpen} onOpenChange={setLanguageOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-6" />
              <div className="px-6 pb-8 safe-area-bottom">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground mb-6">選擇語言</Drawer.Title>
                <div className="space-y-2">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        setSelectedLanguage(lang.code)
                        setLanguageOpen(false)
                      }}
                      className={cn(
                        "w-full flex items-center justify-between p-4 rounded-2xl transition-all",
                        selectedLanguage === lang.code
                          ? "bg-gold/10 border border-gold/30"
                          : "glass-card hover:border-gold/20"
                      )}
                    >
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{lang.flag}</span>
                        <span className="font-semibold text-foreground">{lang.label}</span>
                      </div>
                      {selectedLanguage === lang.code && (
                        <Check className="w-5 h-5 text-gold" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Support Drawer */}
      <Drawer.Root open={supportOpen} onOpenChange={setSupportOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-6" />
              <div className="px-6 pb-8 safe-area-bottom">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground mb-6">支援</Drawer.Title>
                <div className="space-y-3">
                  {supportOptions.map((option, i) => (
                    <button
                      key={i}
                      onClick={() => {
                        if (option.action === 'dispute-navigate' || option.action === 'disputes-list') {
                          setSupportOpen(false)
                          navigate('/disputes')
                        }
                      }}
                      className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:border-gold/20 transition-all group"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-surface-sunken flex items-center justify-center group-hover:bg-gold/10 transition-colors">
                          <option.icon className="w-5 h-5 text-muted-foreground group-hover:text-gold transition-colors" />
                        </div>
                        <div className="text-left">
                          <p className="font-semibold text-sm text-foreground">{option.label}</p>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-gold transition-colors" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Logout Confirmation Drawer */}
      <Drawer.Root open={logoutOpen} onOpenChange={setLogoutOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-6" />
              <div className="px-6 pb-8 safe-area-bottom text-center">
                <div className="w-16 h-16 rounded-full bg-rose-500/10 flex items-center justify-center mx-auto mb-4">
                  <AlertTriangle className="w-8 h-8 text-rose-500" />
                </div>
                <Drawer.Title className="font-display text-xl font-semibold text-foreground mb-2">確認登出？</Drawer.Title>
                <p className="text-sm text-muted-foreground mb-6">您將需要重新登入才能繼續遊戲</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setLogoutOpen(false)}
                    className="flex-1 py-3 glass-card rounded-xl font-semibold text-foreground hover:bg-foreground/5 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => {
                      setLogoutOpen(false)
                      useAuthStore.getState().logout()
                    }}
                    className="flex-1 py-3 bg-rose-500 rounded-xl font-semibold text-white hover:bg-rose-600 transition-colors"
                  >
                    登出
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* Agent Activation Drawer */}
      <Drawer.Root open={agentActivateOpen} onOpenChange={setAgentActivateOpen}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-6" />
              <div className="px-6 pb-8 safe-area-bottom">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-4">
                    <LayoutDashboard className="w-8 h-8 text-gold" />
                  </div>
                  <Drawer.Title className="font-display text-xl font-semibold text-foreground mb-2">成為代理</Drawer.Title>
                  <p className="text-sm text-muted-foreground">建立私人房間，賺取佣金收入</p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="p-4 glass-card rounded-2xl">
                    <p className="font-semibold text-sm text-foreground mb-1">💰 賺取佣金</p>
                    <p className="text-xs text-muted-foreground">從您主持的房間中獲得 0-5% 佣金</p>
                  </div>
                  <div className="p-4 glass-card rounded-2xl">
                    <p className="font-semibold text-sm text-foreground mb-1">🏠 建立私人房間</p>
                    <p className="text-xs text-muted-foreground">最多同時主持 3 個房間</p>
                  </div>
                  <div className="p-4 glass-card rounded-2xl">
                    <p className="font-semibold text-sm text-foreground mb-1">📊 即時追蹤</p>
                    <p className="text-xs text-muted-foreground">查看收益和房間數據</p>
                  </div>
                </div>

                <p className="text-xs text-muted-foreground text-center mb-4">
                  需要最低餘額 $50 才能啟用
                </p>

                <button
                  onClick={() => setAgentActivateOpen(false)}
                  className="w-full py-3 btn-primary rounded-xl font-bold"
                >
                  啟用代理模式
                </button>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

      {/* 2FA Setup Drawer */}
      <Drawer.Root open={twoFAOpen} onOpenChange={(open) => {
        setTwoFAOpen(open)
        if (!open) {
          setTwoFAStep(1)
          setTwoFACode(['', '', '', '', '', ''])
          setTwoFACodeCopied(false)
        }
      }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none max-h-[85vh] flex flex-col" aria-describedby={undefined}>
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 flex flex-col flex-1 overflow-hidden">
              <div className="mx-auto w-12 h-1.5 flex-shrink-0 rounded-full bg-foreground/20 mt-4 mb-4" />
              <div className="px-6 pb-2">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground">兩步驟驗證</Drawer.Title>
                {twoFAStep < 4 && (
                  <p className="text-xs text-muted-foreground mt-1">步驟 {twoFAStep} / 3</p>
                )}
              </div>
              <div className="flex-1 overflow-y-auto px-6 pb-8 safe-area-bottom">
                {twoFAStep === 1 && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-6 text-center">
                      使用驗證器應用程式（如 Google Authenticator）掃描以下 QR Code
                    </p>
                    <div className="flex justify-center mb-6">
                      <div className="w-52 h-52 border-2 border-dashed border-foreground/20 rounded-2xl flex flex-col items-center justify-center gap-3 bg-foreground/5">
                        <QrCode className="w-16 h-16 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground font-medium">掃描 QR Code</span>
                      </div>
                    </div>
                    <div className="p-3 glass-card rounded-xl mb-6">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">手動輸入密鑰</p>
                      <p className="font-mono text-sm text-foreground tracking-widest select-all">JBSW Y3DP EHPK 3PXP</p>
                    </div>
                  </div>
                )}

                {twoFAStep === 2 && (
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground mb-6 text-center">
                      輸入驗證器應用程式中顯示的 6 位數字驗證碼
                    </p>
                    <div className="flex justify-center gap-2 mb-6">
                      {twoFACode.map((digit, i) => (
                        <input
                          key={i}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '')
                            const newCode = [...twoFACode]
                            newCode[i] = val
                            setTwoFACode(newCode)
                            if (val && i < 5) {
                              const next = e.target.parentElement?.children[i + 1] as HTMLInputElement
                              next?.focus()
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Backspace' && !twoFACode[i] && i > 0) {
                              const prev = (e.target as HTMLElement).parentElement?.children[i - 1] as HTMLInputElement
                              prev?.focus()
                            }
                          }}
                          className="w-12 h-14 text-center text-xl font-mono font-bold bg-surface-sunken border border-foreground/10 rounded-xl text-foreground focus:border-gold/50 focus:ring-1 focus:ring-gold/30 outline-none transition-all"
                        />
                      ))}
                    </div>
                    {twoFACode.every(d => d !== '') && twoFACode.join('') !== '123456' && (
                      <p className="text-xs text-rose-400 text-center mb-4">驗證碼不正確，請重試</p>
                    )}
                    <p className="text-xs text-muted-foreground text-center">
                      輸入 123456 進行示範
                    </p>
                  </div>
                )}

                {twoFAStep === 3 && (
                  <div className="mt-4">
                    <div className="p-4 bg-gold/5 border border-gold/20 rounded-2xl mb-4">
                      <p className="text-sm text-gold font-semibold mb-1">請妥善保存備份碼</p>
                      <p className="text-xs text-muted-foreground">如果您無法使用驗證器，可使用備份碼登入。每個備份碼只能使用一次。</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {mockBackupCodes.map((code, i) => (
                        <div key={i} className="p-3 glass-card rounded-xl text-center">
                          <span className="font-mono text-sm text-foreground tracking-wider">{code}</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        navigator.clipboard?.writeText(mockBackupCodes.join('\n'))
                        setTwoFACodeCopied(true)
                        setTimeout(() => setTwoFACodeCopied(false), 2000)
                      }}
                      className={cn(
                        "w-full flex items-center justify-center gap-2 py-3 glass-card rounded-xl font-semibold text-sm transition-all",
                        twoFACodeCopied ? "text-emerald-400 border-emerald-500/20" : "text-foreground hover:border-gold/20"
                      )}
                    >
                      {twoFACodeCopied ? (
                        <><Check className="w-4 h-4" /><span>已複製</span></>
                      ) : (
                        <><Copy className="w-4 h-4" /><span>複製所有備份碼</span></>
                      )}
                    </button>
                  </div>
                )}

                {twoFAStep === 4 && (
                  <div className="mt-8 text-center">
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                      className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-6"
                    >
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ delay: 0.2, type: 'spring', stiffness: 400, damping: 15 }}
                      >
                        <CheckCircle className="w-10 h-10 text-emerald-400" />
                      </motion.div>
                    </motion.div>
                    <h3 className="font-display text-lg font-semibold text-foreground mb-2">兩步驟驗證已啟用</h3>
                    <p className="text-sm text-muted-foreground mb-6">您的帳戶現在受到額外的安全保護</p>
                    <button
                      onClick={() => {
                        setSettings(s => ({ ...s, twoFactorAuth: true }))
                        setTwoFAOpen(false)
                      }}
                      className="w-full py-3 btn-primary rounded-xl font-bold"
                    >
                      完成
                    </button>
                  </div>
                )}

                {twoFAStep < 4 && (
                  <div className="flex gap-3 mt-6">
                    {twoFAStep > 1 && (
                      <button
                        onClick={() => setTwoFAStep(s => s - 1)}
                        className="flex-1 flex items-center justify-center gap-2 py-3 glass-card rounded-xl font-semibold text-foreground hover:bg-foreground/5 transition-colors"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>上一步</span>
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (twoFAStep === 2 && twoFACode.join('') !== '123456') return
                        setTwoFAStep(s => s + 1)
                      }}
                      disabled={twoFAStep === 2 && twoFACode.join('') !== '123456'}
                      className={cn(
                        "flex-1 flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all",
                        twoFAStep === 2 && twoFACode.join('') !== '123456'
                          ? "bg-foreground/5 text-muted-foreground cursor-not-allowed"
                          : "btn-primary"
                      )}
                    >
                      <span>{twoFAStep === 3 ? '完成設定' : '下一步'}</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>

    </motion.div>
  )
}

const themeOptions = [
  { value: 'light' as const, icon: Sun, label: '淺色' },
  { value: 'dark' as const, icon: Moon, label: '深色' },
  { value: 'system' as const, icon: Monitor, label: '系統' },
]

function ThemeToggle() {
  const theme = useThemeStore((s) => s.theme)
  const setTheme = useThemeStore((s) => s.setTheme)

  return (
    <div className="flex rounded-xl bg-surface-sunken p-1 gap-1">
      {themeOptions.map((opt) => {
        const isActive = theme === opt.value
        return (
          <button
            key={opt.value}
            onClick={() => setTheme(opt.value)}
            className={cn(
              "flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all",
              isActive
                ? "bg-gold/20 text-gold border border-gold/30 shadow-sm"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <opt.icon className="w-3.5 h-3.5" />
            {opt.label}
          </button>
        )
      })}
    </div>
  )
}
